import {
  IconHome,
  IconUsers,
  IconChartBar,
  IconSettings,
  IconSwimming,
  IconMessage,
  IconCoin,
  IconLogin,
  IconUserPlus,
  IconBuilding,
  IconUsersGroup,
  IconMedal,
  IconMail,
  IconServer,
  IconClipboardList,
} from '@tabler/icons-react';
import { PreRegistrationStatsBadge } from '@/features/customer-pre-registrations';

export interface NavigationItem {
  link?: string;
  label?: string;
  icon?: React.ComponentType<{ size?: string | number; color?: string; stroke?: number }>;
  color?: string;
  badge?: string | React.ReactNode;
  badgeColor?: string;
  children?: NavigationItem[];
  roles?: string[]; // 表示可能なロール
  storeIds?: number[]; // 表示可能な店舗ID（未指定=全店舗表示）
  divider?: boolean; // 区切り線を表示
  openInNewWindow?: boolean; // 新しいウィンドウで開く
}

/**
 * ナビゲーションメニュー構成
 * 優先順位順に配置
 */
export const navigationItems: NavigationItem[] = [
  // ==================
  // 日常業務
  // ==================
  {
    link: '/dashboard',
    label: 'ダッシュボード',
    icon: IconHome,
    color: 'blue',
  },
  {
    link: '/entry-exit',
    label: '入退室管理',
    icon: IconLogin,
    color: 'cyan',
    children: [
      { link: '/entry-exit/check-in', label: '入室' },
      { link: '/entry-exit/check-out', label: '退室' },
      { link: '/entry-exit/current-status', label: '滞在状況' },
      { link: '/entry-exit/attendance-list', label: '入退室一覧' },
      { link: '/entry-exit/attendance-monitor', label: '入退室モニター', openInNewWindow: true },
    ],
  },
  {
    link: '/customers',
    label: '顧客管理',
    icon: IconUsers,
    color: 'teal',
  },

  {
    link: '/school-management',
    label: 'スクール管理',
    icon: IconSwimming,
    color: 'cyan',
    children: [
      { link: '/school-management/kids-attendance', label: '児童出席簿' },
      { link: '/lane-assignment/class-lanes', label: 'クラス別レーン配置' },
      { link: '/lane-assignment/student-lanes', label: 'クラス別レーン一覧' },
      { link: '/tablet-entry', label: '児童出席管理', openInNewWindow: true },
      { link: '/tablet-grade-promotion', label: '児童進級管理', openInNewWindow: true },
      { link: '/school-management/grade-judgment', label: '児童評価判定' },
      { link: '/school-management/monthly-evaluation-results', label: '月別評価結果' },
    ],
  },
  { divider: true, roles: ['admin', 'manager'] }, // スタッフには非表示

  // ==================
  // 請求・入金
  // ==================
  {
    link: '/billing',
    label: '請求管理',
    icon: IconCoin,
    color: 'green',
    roles: ['admin', 'manager'], // スタッフには非表示
    children: [
      // === 請求処理 ===
      { link: '/billing', label: '請求サイクル管理' },
      { link: '/billing/unpaid', label: '未入金者リスト' },
      { link: '/billing/adjustments', label: '料金調整マスタ' },
      { link: '/discount-groups', label: 'グループマスタ', icon: IconUsersGroup, color: 'violet' },
    ],
  },
  {
    link: '/reports',
    label: 'レポート',
    icon: IconChartBar,
    color: 'grape',
    roles: ['admin', 'manager'], // スタッフは非表示
    children: [
      // === 現場スタッフ向け ===
      { link: '/reports/member-composition', label: '会員構成表' },
      { link: '/reports/daily', label: '日次運営サマリ' },
      { link: '/reports/daily-cumulative', label: '日次累計表' },
      { link: '/reports/daily-business', label: '日報' },
      { link: '/reports/monthly-business', label: '月報' },
      { link: '/reports/user-count', label: '利用者人数表' },
      { link: '/reports/course-grade', label: 'コース別人数一覧' },
      { link: '/reports/school-enrollment', label: '学校別在籍人数表' },
      { divider: true },

      // === 自由分析 ===
      { link: '/reports/sales-transition', label: '売上推移表' },
      { link: '/reports/member-achievement', label: '入退会達成率' },
      { divider: true },

      // === 設定 ===
      { link: '/reports/excluded-members', label: '集計除外設定' },
    ],
  },
  { divider: true },

  // ==================
  // コミュニケーション
  // ==================
  {
    link: '/communication',
    label: 'コミュニケーション',
    icon: IconMessage,
    color: 'blue',
    children: [
      { link: '/bulletin-boards', label: '掲示板' },
      { link: '/chat', label: 'チャット' },
    ],
  },
  {
    link: '/customer-pre-registrations',
    label: '事前登録',
    icon: IconUserPlus,
    color: 'teal',
    badge: <PreRegistrationStatsBadge variant="count" />,
  },
  {
    link: '/email-broadcasts',
    label: 'メール配信',
    icon: IconMail,
    color: 'indigo',
    children: [
      { link: '/email-broadcasts', label: '配信一覧' },
      { link: '/email-broadcasts/new', label: '新規配信' },
      { link: '/email-broadcasts/templates', label: 'テンプレート管理' },
    ],
  },
  {
    link: '/promotion-tests',
    label: '進級テスト名簿',
    icon: IconClipboardList,
    color: 'orange',
    storeIds: [1], // 下上津役校専用
    roles: ['admin', 'manager'],
    children: [
      { link: '/promotion-tests', label: '開催一覧' },
      { link: '/promotion-tests/titles', label: '表題マスタ' },
    ],
  },
  { divider: true },

  // ==================
  // マスタ管理（管理者向け）
  // ==================
  {
    link: '/master',
    label: 'マスタ管理',
    icon: IconSettings,
    color: 'gray',
    roles: ['admin', 'manager', 'staff'],
    children: [
      { link: '/master/stores', label: '店舗管理' },
      { link: '/master/facilities', label: '施設管理', icon: IconBuilding },
      { link: '/master/staffs', label: 'スタッフ管理' },
      { link: '/master/program-categories', label: 'プログラムカテゴリ' },
      { link: '/master/programs', label: 'プログラム設定' },
      { link: '/master/grades', label: '級マスタ' },
      { link: '/master/schools', label: '学校マスタ' },
      { link: '/master/banks', label: '金融機関マスタ' },
      { divider: true, roles: ['admin'] },
      { link: '/master/system/workers', label: 'ワーカー管理', icon: IconServer, roles: ['admin'] },
    ],
  },
];

/**
 * ロール・店舗IDに基づいてナビゲーションアイテムをフィルタリング
 */
export const filterNavigationByRole = (
  items: NavigationItem[],
  userRole?: string,
  currentStoreId?: number
): NavigationItem[] => {
  return items.filter((item) => {
    // 店舗ID制限がある場合、現在の店舗が含まれていなければ非表示
    if (item.storeIds && item.storeIds.length > 0) {
      if (!currentStoreId || !item.storeIds.includes(currentStoreId)) {
        return false;
      }
    }
    // ロール制限がない場合は表示
    if (!item.roles || item.roles.length === 0) {
      return true;
    }
    // ユーザーのロールが含まれている場合は表示
    return userRole && item.roles.includes(userRole);
  });
};

/**
 * 現在のパスに基づいてアクティブなアイテムを取得
 */
export const getActiveItem = (
  items: NavigationItem[],
  currentPath: string
): NavigationItem | null => {
  for (const item of items) {
    if (item.link === currentPath) {
      return item;
    }
    if (item.children) {
      const childItem = getActiveItem(item.children, currentPath);
      if (childItem) {
        return childItem;
      }
    }
  }
  return null;
};

/**
 * パンくずリスト用のパスを生成
 */
export const getBreadcrumbPath = (
  items: NavigationItem[],
  currentPath: string,
  path: NavigationItem[] = []
): NavigationItem[] => {
  for (const item of items) {
    if (item.link === currentPath) {
      return [...path, item];
    }
    if (item.children) {
      const childPath = getBreadcrumbPath(item.children, currentPath, [...path, item]);
      if (childPath.length > path.length + 1) {
        return childPath;
      }
    }
  }
  return path;
};
